import { Avatar, AvatarGroup, Badge, Box, IconButton, InputAdornment, List, ListItem, ListItemButton, TextField, Typography } from '@mui/material';
import GroupsSkeleton from '__mocks__/skeletonLoading/groupsLoader';
import { VerifiedBadge } from 'assets';
import { NewChatIcon , NoConversations, UpArrowIcon, DownArrowIcon, SearchBarIcon, CloseChatIcon } from 'assets/icon';
import { ApiPaths, ApiPaths_Groups, IUserDetails } from 'models/apiPaths';
import { IUserGroupSearch } from 'models/groups';
import { IFriendsList } from 'models/userProfile';
import AvatarImg from 'pages/components/avatarImg';
import TypographyWithI18N from 'pages/components/TypographyWithI18N';
import { useEffect, useRef, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useNavigate } from 'react-router-dom';
import { axiosGetGroupPost, axiosPostGroupSearch, createConversation, createGroupConversation, fetchUserDetails, getAllConversation, getConversationSnapShot, searchByMessage, updateDeliveredStatus } from 'services/api';
import { RootState } from 'store';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { clearMessages } from 'store/signalrSlice';
import { setGroupNameData } from 'store/utilsSlice';
import { colors } from 'styles/colors';
import { buildSlugId, getPublicId } from 'utilities/helpers';
import { getUrl, groupImgUrl } from 'utilities/userImageURL';
import ChatPopup from './chatPopup';
import CreateMessagePopup from './createMessagePopup';

interface IFriendItemProps {
  loggedUser: any;
  friends: any[];
  taggedGroup: any;
  conversation: any;
  onClick: (conversationId: any, friends: any) => void;
}

const FriendItem = ({ loggedUser, friends, taggedGroup, conversation, onClick }: IFriendItemProps) => {

  const getDisplayName = (friends: any[]) => {
    if (friends.length === 2) {
      let receivers = friends.filter(friend => friend.userId !== loggedUser?.id);
      return receivers[0]?.username ?? '';
    }
    if (friends.length === 3) return `${friends[0].username}, ${friends[1].username}, ${friends[2].username} `;
    if (friends.length > 3) {
      return `${friends[0].username}, ${friends[1].username}, +${friends.length - 2}`;
    }
    return '';
  };

  const stringToColor = (string: string) => {
    let hash = 0;
    for (let i = 0; i < string?.length; i += 1) {
      hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }
    let color = '#';
    for (let i = 0; i < 3; i += 1) {
      const value = (hash >> (i * 8)) & 0xff;
      color += `00${value.toString(16)}`.slice(-2);
    }
    return color;
  };

  const getInitials = (name: string) => {
    return name && name
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase())
      .join('').substring(0, 2);
  };

  const renderAvatar = (friends: any[]) => {
    if (friends.length === 3) {
      return friends.map((friend, index) => (
        <Avatar
          key={index}
          alt={friend?.username ?? ''}
          src={friend.imageId ? getUrl(friend.imageId) : undefined}
          sx={{
            objectFit: 'cover',
            borderRadius: '50%',
            border: `2px solid ${colors.border.primary}`,
            marginLeft: -30,
            zIndex: index,
            width: friends.length === 1 ? 40 : 30,
            height: friends.length === 1 ? 40 : 30,
            bgcolor: friend.imageId ? undefined : stringToColor(friend.username),
          }}
        >
          {!friend.imageId && getInitials(friend.username)}
        </Avatar>
      ))
    }
    else {
      return [
        <Avatar
          key="friend-0"
          alt={friends[0].username}
          src={friends[0].imageId ? getUrl(friends[0].imageId) : undefined}
          sx={{
            objectFit: 'cover',
            borderRadius: '50%',
            border: `2px solid ${colors.border.primary}`,
            width: 30,
            height: 30,
            zIndex: 1,
            bgcolor: friends[0].imageId ? undefined : stringToColor(friends[0].username),
          }}
        >
          {!friends[0].imageId && getInitials(friends[0].username)}
        </Avatar>,
                <Avatar
          key="friend-1"
          alt={friends[1]?.username}
          src={friends[1]?.imageId ? getUrl(friends[1]?.imageId) : undefined}
          sx={{
            objectFit: 'cover',
            borderRadius: '50%',
            border: `2px solid ${colors.border.primary}`,
            width: 30,
            height: 30,
            zIndex: 2,
            bgcolor: friends[1]?.imageId ? undefined : stringToColor(friends[1]?.username),
          }}
        >
          {!friends[1]?.imageId && getInitials(friends[1]?.username)}
        </Avatar>,
        <Avatar
          key="count"
          sx={{
            objectFit: 'cover',
            borderRadius: '50%',
            border: `2px solid ${colors.border.primary}`,
            backgroundColor: colors.text.primaryTheme,
            width: 30,
            height: 30,
            zIndex: 3,
            fontSize: 14,
          }}
        >
          +{friends.length - 2}
        </Avatar>
      ]
    }
  };

  const getAvatars = (friends: any[]) => {

    let friend;
    if (friends.length === 2) {
      friend = friends.find(friend => friend.userId !== loggedUser?.id);
    }

    return (
      friends.length === 2 ?
        (
          <Badge
            sx={{
              '& .MuiBadge-dot': {
                borderRadius: '50%',
                width: friend?.onlineStatus && !conversation.isAdminConversation ? 10 : 0,
                height: friend?.onlineStatus && !conversation.isAdminConversation ? 10 : 0,
                border: friend?.onlineStatus && !conversation.isAdminConversation ? `1px solid ${colors.text.primaryWhite}` : '',
                backgroundColor: '#4caf50',
              },
            }}
            variant="dot"
            overlap="circular"
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            {<Avatar
              alt={friend.username}
              src={friend.imageId ? getUrl(friend.imageId) : undefined}
              sx={{
                objectFit: 'cover',
                borderRadius: '50%',
                border: `2px solid ${colors.border.primary}`,
                zIndex: 1,
                width: 30,
                height: 30,
                bgcolor: friend.imageId ? undefined : stringToColor(friend.username),
              }}
            >
              {!friend.imageId && getInitials(friend.username)}
            </Avatar>}
          </Badge>) :
        <AvatarGroup max={3} spacing={30}>
          {renderAvatar(friends)}
        </AvatarGroup>
    );
  };

  const formatTimeDifference = (): string => {
    const updatedDate = new Date(conversation.modifiedAt);
    const currentDate = new Date();

    const isToday = updatedDate.toDateString() === currentDate.toDateString();

    const yesterday = new Date();
    yesterday.setDate(currentDate.getDate() - 1);
    const isYesterday = updatedDate.toDateString() === yesterday.toDateString();

    if (isToday) {
      return updatedDate.toLocaleTimeString(undefined, {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      }).toLowerCase();
    } else if (isYesterday) {
      return "Yesterday";
    } else {
      return updatedDate.toLocaleDateString(undefined, {
        day: "2-digit",
        month: "2-digit",
      });
    }
  };

  const getLastMessage = () => {
    if (conversation.isAdminConversation) {
      return (stripHtml(conversation.lastMessage.content));
    }
    if (conversation.lastMessage != null && conversation.lastMessage.senderId != null && conversation.lastMessage.content != null) {
      if (conversation.lastMessage.senderId === loggedUser?.id) {
        return ("You: " + conversation.lastMessage.content);
      }
      else {
        const sender = friends.find(x => x.userId === conversation.lastMessage.senderId);
        const firstName = sender.username.split(" ")[0];
        return (`${firstName}: ` + conversation.lastMessage.content);
      }
    }
    return '';
  }

  const stripHtml = (html: string) => {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
  };

  const getVerifiedBadge = () => {
    const receivers = friends.filter(friend => friend.userId !== loggedUser?.id);
    if (
      friends.length === 2 &&
      receivers.length > 0 &&
      (receivers[0].roles.includes('SWM_SENDER') || receivers[0].roles.includes('APP_ACCOUNT'))
    ) {
      return (
        <IconButton color="inherit" sx={{ p: 0, ml: '4px' , mb:'5px' }}>
          <VerifiedBadge style={{ width: 17, height: 17 }} />
        </IconButton>
      );
    }
    return null;
  };

  return (
    <ListItem disablePadding>
      <ListItemButton
        onClick={() => onClick(conversation.conversationId, friends)}
        style={{ paddingRight: '10px' }}
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Box display='flex' alignItems='center'>
          <Box display='flex' justifyContent='start' sx={{ width: '30px' }}>
            {getAvatars(friends)}
          </Box>
          <Box display='flex' flexDirection='column' marginLeft={2} sx={{
            overflow: 'hidden',
          }}>
            <Box display='flex' alignItems='center'>
              <TypographyWithI18N
                className={conversation.unreadCount !== 0 ? 'bodyTextSmallBold' : 'bodyTextSmall'}
                i18nKey={getDisplayName(friends)}
                sx={{
                  color: colors.text.primaryBlack,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  maxWidth: (conversation.isAdminConversation &&
                    conversation.isGroupTagged) ||
                    friends.length > 2
                    ? conversation.isAdminConversation &&
                      conversation.isGroupTagged
                      ? 90
                      : 200
                    : 'none',
                  textOverflow:
                    (conversation.isAdminConversation &&
                      conversation.isGroupTagged) ||
                      friends.length > 2
                      ? 'ellipsis'
                      : 'clip'
                }} />
              {getVerifiedBadge()}
              {conversation.isAdminConversation && conversation.isGroupTagged && (
                <Box
                  className="bodyText"
                  sx={{
                    marginLeft: '5px',
                    color: colors.text.primaryBlack,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Typography alignItems='center' component="span">in&nbsp;</Typography>
                  <AvatarImg
                    id="userImg"
                    enableLetterImg={!taggedGroup?.groupImageDto.id}
                    alt={taggedGroup?.name}
                    src={groupImgUrl(taggedGroup?.groupImageDto.id)}
                    name={taggedGroup?.name}
                    nameStyle={{
                      fontSize: 10,
                    }}
                    width={20}
                    height={20}
                    style={{
                      objectFit: 'cover',
                      borderRadius: '50%',
                      marginRight: '1px',
                    }}
                  />
                  <TypographyWithI18N
                    className={conversation.unreadCount !== 0 ? 'bodyTextSmallBold' : 'bodyTextSmall'}
                    i18nKey={taggedGroup?.name}
                    sx={{
                      marginLeft: '5px',
                      color: colors.text.primaryBlack,
                      width: 100,
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                    }}
                  />
                </Box>
              )}
            </Box>
            <Typography
              fontSize={12}
              sx={{
                color: colors.text.approxSuvaGrey,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                width: 200,
                maxWidth: '100%',
                textOverflow: 'ellipsis',
                '& p': { margin: 0 },
              }}
              dangerouslySetInnerHTML={{ __html: getLastMessage() }}
            />
          </Box>
        </Box>
        <Box display='flex' flexDirection='column' sx={{ marginRight: '14px', alignItems: 'flex-end', minWidth: 'max-content', flexShrink: 0 }}>
          <TypographyWithI18N
            i18nKey={formatTimeDifference()}
            fontSize={12}
            sx={{
              color: colors.text.approxSuvaGrey, whiteSpace: 'nowrap', minWidth: 'max-content', flexShrink: 0
            }} />
          {conversation.unreadCount !== 0 && <Box
            sx={{
              display: "inline-flex",
              justifyContent: "center",
              alignItems: "center",
              backgroundColor: "primary.main",
              color: "primary.contrastText",
              borderRadius: "15px",
              height: "24px",
              width: "24px",
              fontSize: 14,
              boxShadow: 1,
            }}>
            <TypographyWithI18N
              i18nKey={conversation.unreadCount}
              fontSize={12}
              fontWeight={500}
              color={colors.text.primaryWhite}
            />
          </Box>}
        </Box>
      </ListItemButton>
    </ListItem>
  );
};

export interface IOpenDialog {
  open: boolean;
  id?: string;
  loaderBtn?: boolean;
  successModal?: boolean;
}

const ChatsScreen = () => {
  const navigate = useNavigate();
  const [friendData, setFriendData] = useState<any[]>([]);
  const [taggedGroups, setTaggedGroupsData] = useState<any[]>([]);
  const [conversationsData, setConversationsData] = useState<any[]>([]);
  const [loggedUser, setLoggedUser] = useState<IUserDetails>();
  const [loader, setLoader] = useState<boolean>(false);
  const [scroll, setScroll] = useState<boolean>(true);
  const [hasFetchedData, setHasFetchedData] = useState(false);
  const [activeConversations, setActiveConversations] = useState<{ conversationId: string; friends: IFriendsList[] }[]>([]);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [pageChange, setPageChange] = useState({
    previousPageCursor: '',
    nextPageCursor: '',
    nextPage: false
  });
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerHeight, setContainerHeight] = useState(650);
  const dispatch = useAppDispatch();
  const signalr = useAppSelector((state: RootState) => state.signalr);
  const [isCreateMessageOpen, setIsCreateMessageOpen] = useState(false);
  const [totalUnreadCount, setTotalUnreadCount] = useState<number>(0);

  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [userSearchResults, setUserSearchResults] = useState<any[]>([]);
  const [messageSearchResults, setMessageSearchResults] = useState<any[]>([]);

  const handleUserGroupSearch = async (text: string) => {
    setSearchQuery(text);
    if (!text.trim()) {
      setUserSearchResults([]);
      setMessageSearchResults([]);
      setIsSearching(false);
      return;
    }
    setIsSearching(true);
    const payload = {
      keyword: text,
      enforceGroupMembership: false,
      enforceFriendship: false
    };
    try {
      const [groupSearchResponse, messageSearchResponse] = await Promise.all([
        axiosPostGroupSearch(ApiPaths_Groups.GROUP_USER_SEARCH, payload),
        searchByMessage(text)
      ]);

      const items: IUserGroupSearch[] = groupSearchResponse || [];
      const users = items
        .filter((_itm: any) => _itm.user)
        .map((_itm: any) => {
          return {
            userActive: Boolean(_itm.user),
            id: _itm.user?.id,
            name: _itm.user?.summary?.userName || _itm.user?.username,
            imageId: _itm.user?.userImage?.id,
            imageUrl: _itm.user?.userImage?.id ? getUrl(_itm.user?.userImage?.id) : ''
          };
        });

      setUserSearchResults(users);
      setMessageSearchResults(
        (messageSearchResponse || []).filter(
          (_itm: any) => !_itm.isGroup && !_itm.groupId && !_itm.groupConversationId
        )
      );
    } catch (error) {
      console.error('Error during search:', error);
    } finally {
      setIsSearching(false);
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
    setUserSearchResults([]);
    setMessageSearchResults([]);
    setIsSearching(false);
  };

  const handleUserResultClick = async (_itm: any) => {
    try {
      const apiPath = `${ApiPaths.GET_USER}/${_itm.id}`;
      const userDetail = await fetchUserDetails(apiPath);
      if (userDetail.friend) {
        const newConversation = await createConversation([
          loggedUser?.id,
          _itm.id
        ]);
        const convId = newConversation.id || newConversation.conversationId;
        const snap = await getConversationSnapShot(convId).catch(() => null);
        const friends = snap?.participants || [
          { userId: loggedUser?.id, username: loggedUser?.username, imageId: loggedUser?.userImage?.id },
          { userId: _itm.id, username: _itm.name || _itm.username, imageId: _itm.imageId }
        ];
        if (snap?.participants) {
          setFriendData((prevFriends = []) => {
            const combined = [...prevFriends, ...snap.participants];
            return Array.from(new Map(combined.map((f) => [f.userId, f])).values());
          });
        }
        handleConversationClick(convId, friends);
        clearSearch();
      } else {
        navigate(`/profile/${buildSlugId(_itm?.name, getPublicId(_itm, _itm?.id))}`);
        clearSearch();
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleMessageResultClick = async (_itm: any) => {
    const conversationId = _itm.conversationId;
    try {
      const snap = await getConversationSnapShot(conversationId).catch(() => null);
      const friends = snap?.participants || _itm.participants || [];
      if (snap?.participants) {
        setFriendData((prevFriends = []) => {
          const combined = [...prevFriends, ...snap.participants];
          return Array.from(new Map(combined.map((f) => [f.userId, f])).values());
        });
      }
      handleConversationClick(conversationId, friends);
      clearSearch();
    } catch (err) {
      console.error(err);
      handleConversationClick(conversationId, _itm.participants || []);
      clearSearch();
    }
  };

  const renderAvatarsForParticipants = (participants: any[]) => {
    if (!participants || participants.length === 0) return null;
    const friend = participants.find((p) => p.userId !== loggedUser?.id) || participants[0];
    return (
      <AvatarImg
        id="userImg"
        enableLetterImg={!friend?.imageId}
        alt={friend?.username || ''}
        src={friend?.imageId ? getUrl(friend.imageId) : ''}
        name={friend?.username || ''}
        width={30}
        height={30}
        nameStyle={{ fontSize: 10 }}
        style={{ borderRadius: '50%', objectFit: 'cover' }}
      />
    );
  };

  const getDisplayNameForParticipants = (participants: any[]) => {
    if (!participants || participants.length === 0) return '';
    if (participants.length === 2) {
      const receiver = participants.find((p) => p.userId !== loggedUser?.id);
      return receiver?.username || participants[0]?.username || '';
    }
    if (participants.length === 3) {
      return `${participants[0].username}, ${participants[1].username}, ${participants[2].username}`;
    }
    if (participants.length > 3) {
      return `${participants[0].username}, ${participants[1].username}, +${participants.length - 2}`;
    }
    return participants[0]?.username || '';
  };

  const formatTimeDifferenceForMsg = (msg: any): string => {
    if (!msg || !msg.updatedAt) return '';
    const updatedDate = new Date(msg.updatedAt);
    const currentDate = new Date();

    const isToday = updatedDate.toDateString() === currentDate.toDateString();

    const yesterday = new Date();
    yesterday.setDate(currentDate.getDate() - 1);
    const isYesterday = updatedDate.toDateString() === yesterday.toDateString();

    if (isToday) {
      return updatedDate.toLocaleTimeString(undefined, {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      }).toLowerCase();
    } else if (isYesterday) {
      return 'Yesterday';
    } else {
      return updatedDate.toLocaleDateString(undefined, {
        day: '2-digit',
        month: '2-digit'
      });
    }
  };

  const HighlightedMessage = (conversation: any, keyword: string, snippetLength = 30) => {
    let message = conversation.message?.content || '';
    const keywordIndex = message.toLowerCase().indexOf(keyword.toLowerCase());

    if (keywordIndex === -1) {
      return (
        <Typography
          fontSize={12}
          sx={{
            color: colors.text.approxSuvaGrey,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: 150
          }}
        >
          {message}
        </Typography>
      );
    }

    const start = Math.max(0, keywordIndex - snippetLength / 2);
    const end = Math.min(message.length, keywordIndex + snippetLength / 2);
    const snippet = message.substring(start, end);

    const parts = snippet.split(new RegExp(`(${keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'));

    let senderName = '';

    if (
      conversation.message?.senderId != null &&
      conversation.message?.content != null
    ) {
      if (conversation.message.senderId === loggedUser?.id) {
        senderName = 'You: ';
      } else if (conversation.participants) {
        const sender = conversation.participants.find(
          (x: any) => x.userId === conversation.message.senderId
        );
        if (sender?.username) {
          const firstName = sender.username.split(' ')[0];
          senderName = `${firstName}: `;
        }
      }
    }

    return (
      <Typography
        fontSize={12}
        sx={{
          color: colors.text.approxSuvaGrey,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          maxWidth: 150
        }}
      >
        {senderName}
        {start > 0 && '..'}
        {parts.map((part: any, index: any) =>
          part.toLowerCase() === keyword.toLowerCase() ? (
            <span
              key={index}
              style={{
                color: colors.text.primaryTheme,
                fontWeight: 'bold'
              }}
            >
              {part}
            </span>
          ) : (
            part
          )
        )}
        {end < message.length && '..'}
      </Typography>
    );
  };


  useEffect(() => {
    if (friendData != null) {
      setFriendData((prevData) => {
        return prevData.map((friend) => {
          if (friend.userId === signalr.userStatus.userid) {
            return {
              ...friend,
              onlineStatus: signalr.userStatus.isonline
            };
          }
          return friend;
        });
      });
    }
  }, [signalr.userStatus]);

  const handleCloseChat = (conversationId: string) => {
    setActiveConversations(activeConversations.filter((activeConversation) => activeConversation.conversationId !== conversationId));

    getConversationSnapShot(conversationId).then((data) => {
      if (data.deletedBy?.includes(loggedUser?.id)) {
        setConversationsData((prev) =>
          prev.filter((conv) => conv.conversationId !== conversationId)
        );
      } else {
        const unreadCount = data.unreadCount;
        const previousUnread = conversationsData.find((c) => c.conversationId === conversationId)?.unreadCount ?? 0;
        setTotalUnreadCount((prev) => Math.max(0, prev - (previousUnread - unreadCount)));
        setConversationsData((prev) =>
          prev.map((conv) =>
            conv.conversationId === conversationId
              ? { ...conv, unreadCount }
              : conv
          )
        );
      }
    });
  };

  useEffect(() => {
    const updateHeight = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setContainerHeight(rect.height);
      }
    };

    updateHeight();
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, []);

  const handleConversationClick = (conversationId: string, friends: any) => {
    if (!activeConversations.find((activeConversation) => activeConversation.conversationId === conversationId)) {
      if (activeConversations.length >= 3) {
        setActiveConversations(prevConversations => [
          ...prevConversations.slice(1),
          { conversationId, friends },
        ]);
      } else {
        setActiveConversations([
          ...activeConversations,
          { conversationId, friends }
        ]);
      }
    }
  };

  const onScrollEvent = (e: any) => {
    setScroll(!scroll);
  };

  const userProfileDetails = async () => {
    const userProfile = await fetchUserDetails(ApiPaths.ME);
    setLoggedUser(userProfile);
    getConversationsList();
    updateDeliveredStatus();
  };

  const handleIncomingMessage = (newMessage: any) => {
    if (newMessage != null || newMessage.length === 0) {
      const conversationId = newMessage.conversationId;
      const message = newMessage.message;

      getConversationSnapShot(conversationId).then((data) => {
        setTotalUnreadCount((prev) => prev + 1);
        if (data?.participants) {
          setFriendData((prevFriends = []) => {
            const combinedFriends = [...prevFriends, ...data.participants];

            const uniqueFriends = Array.from(
              new Map(combinedFriends.map((friend) => [friend.userId, friend])).values()
            );

            return uniqueFriends;
          });
          if (data.isAdminConversation && data.isGroupTagged) {
            setTaggedGroupsData((prevGroups = []) => {
              const combinedGroups = [...prevGroups, data.group];
              const uniqueGroups = Array.from(new Map(combinedGroups.map((group) => [group.id, group])).values());
              return uniqueGroups;
            });
          }
        }

        setConversationsData((prevConversations) => {
          const updatedConversations = prevConversations.map((conversation) =>
            conversation.conversationId === conversationId
              ? {
                ...conversation,
                lastMessage: {
                  content: message.content,
                  senderId: message.senderId,
                  updatedAt: message.createdAt
                },
                modifiedAt: message.createdAt,
                unreadCount: (conversation.unreadCount || 0) + 1,
              }
              : conversation
          );

          const conversationExists = prevConversations.some(
            (conversation) => conversation.conversationId === conversationId
          );

          const newConversations = conversationExists
            ? updatedConversations
            : [
              ...updatedConversations,
              {
                conversationId: conversationId,
                lastMessage: message,
                deletedBy: [],
                modifiedAt: message.createdAt,
                participantIds: [message.senderId, ...message.participantIds],
                unreadCount: 1,
                groupId: data.groupId,
                isAdminConversation: data.isAdminConversation,
                isGroupTagged: data.isGroupTagged
              },
            ];

          return newConversations.sort(
            (a, b) => new Date(b.modifiedAt).getTime() - new Date(a.modifiedAt).getTime()
          );
        });
        dispatch(clearMessages());
      })
    }
  };

  const handleChatPopupMessageSent = (conversationId: string, content: string) => {
    setConversationsData((prev) => {
        const now = new Date().toISOString();
        const updated = prev.map((conv) =>
            conv.conversationId === conversationId
                ? {
                    ...conv,
                    lastMessage: {
                        ...conv.lastMessage,
                        content,
                        senderId: loggedUser?.id,
                        updatedAt: now,
                    },
                    modifiedAt: now,
                    unreadCount: 0,
                }
                : conv
        );
        return updated.sort(
            (a, b) => new Date(b.modifiedAt).getTime() - new Date(a.modifiedAt).getTime()
        );
    });
  };

  useEffect(() => {
    if (signalr.messages && !(Array.isArray(signalr.messages) && signalr.messages.length === 0)) {
      handleIncomingMessage(signalr.messages);
    }
  }, [signalr.messages]);

  const getConversationsList = () => {
    setLoader(true);
    const payload = {
      size: 20,
      nextPageCursor: pageChange?.nextPageCursor ?? ''
    };
    getAllConversation(payload)
      .then((data) => {
        const res = data.content;
        const conversations = res.conversationHistory.filter(
          (conversation: any) => conversation.lastMessage != null && conversation.lastMessage.content !== null
        );
        const participantsData = res.participants || [];
        const total = res.totalUnreadCount ?? data.totalUnreadCount;
          if (typeof total === 'number') setTotalUnreadCount(total);

        setPageChange({
          previousPageCursor: data.previousPageCursor,
          nextPageCursor: data.nextPageCursor,
          nextPage: data.nextPageCursor ? true : false
        });

        const groupsData = res.groups || [];
        setTaggedGroupsData(prev => {
          const newGroups = groupsData.filter((group: any) =>
            !prev.some(existingGroup => existingGroup.id === group.id)
          );
          return [...prev, ...newGroups];
        });
        let loggeduserData = {
          userId: res.userId,
          username: res.username,
          imageId: res.imageId
        };

        setFriendData(prev => {
          const allNewParticipants = [...participantsData, loggeduserData];
          const newParticipants = allNewParticipants.filter((participant: any) =>
            !prev.some(existingParticipant => existingParticipant.userId === participant.userId)
          );
          return [...prev, ...newParticipants];
        });

        if (conversations && conversations.length > 0) {
          setConversationsData(prev => {
            const newConversations = conversations.filter((convo: any) =>
              !prev.some(existingConvo => existingConvo.conversationId === convo.conversationId)
            );
            return [...prev, ...newConversations];
          });
        }
      })
      .catch((err) => {
        console.error("Error fetching conversations:", err);
      })
      .finally(() => {
        setLoader(false);
        setHasFetchedData(true);
      });
  };

  useEffect(() => {
    userProfileDetails();
  }, []);

  const toggleExpand = () => {
  setIsExpanded(!isExpanded);
};

  const openChatPopup = (conversationId: string, retry = 0) => {
  const conversation = conversationsData.find(
      (c) => c.conversationId === conversationId
    );
      if (!conversation) {
          if (retry < 5) {
          setTimeout(() => openChatPopup(conversationId, retry + 1), 300);
        }
      return;
      }
  const friends = conversation.participantIds
      .map((id: any) => friendData.find((f) => f.userId === id))
      .filter(Boolean);
      handleConversationClick(conversationId, friends);
  };

  return (
      <Box 
      sx={{ position: 'fixed',bottom: 0,right: 10,maxWidth:'350px',width: '100%',backgroundColor: colors.background.approxAliceBlue,borderRadius: '8px 8px 0 0',boxShadow: '0 -2px 10px rgba(0,0,0,0.1)',zIndex: 1000,
      display: 'flex',flexDirection: 'column',maxHeight: isExpanded ? '400px' : '56px',transition: 'max-height 0.3s ease-in-out',overflow: 'hidden'
    }}
  > 
      <Box
      onClick={toggleExpand}
        sx={{
          height: '56px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          borderBottom: isExpanded ? `1px solid ${colors.border.primary}` : 'none',
          flexShrink: 0,
          '&:hover': {
          backgroundColor: 'rgba(0,0,0,0.02)'
        }
        }}
      >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <AvatarImg
          id="chat-header-user-avatar"
          enableLetterImg={!loggedUser?.userImage?.id}
          src={loggedUser?.userImage?.id ? getUrl(loggedUser.userImage.id) : ''}
          alt="user profile"
          name={loggedUser?.username ?? ''}
          width={30}
          height={30}
          nameStyle={{ fontSize: 12 }}
          style={{
          borderRadius: '50%',
          objectFit: 'cover',
          background: colors.text.approxLightShade,
          }}
        />
        <TypographyWithI18N
          className="smallHeaderBold"
          i18nKey={'Messaging'}
          sx={{
            color: colors.text.primaryBlack,
            whiteSpace: 'nowrap',
          }}
        />
        {totalUnreadCount > 0 && (
            <Box
                sx={{
                    display: 'inline-flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    backgroundColor: 'primary.main',
                    color: 'primary.contrastText',
                    borderRadius: '50%',
                    width: 25,
                    height: 25,
                    fontSize: 12,
                    fontWeight: 500,
                }}
            >
                {totalUnreadCount > 99 ? '99+' : totalUnreadCount}
            </Box>
        )}
      </Box>
      
      <Box display="flex" alignItems="center" gap={1}>
      {isExpanded && (
        <IconButton
            size="small"
            onClick={(e) => {
            e.stopPropagation();
            setIsCreateMessageOpen(true);
        }}
        >
            <NewChatIcon />
        </IconButton>
      )}
        <IconButton size="small" sx={{ p: 0 }}>
            {isExpanded ? <DownArrowIcon /> : <UpArrowIcon />}
        </IconButton>
      </Box>

      </Box>

      {isExpanded && (
        <>
          <Box sx={{ px: 2, py: 1.2, backgroundColor: '#fff' }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search messages"
              value={searchQuery}
              onChange={(e) => handleUserGroupSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ mr: 1, ml: 0.5 }}>
                    <SearchBarIcon style={{ width: 20, height: 20, opacity: 0.55 }} />
                  </InputAdornment>
                ),
                endAdornment: searchQuery ? (
                  <InputAdornment position="end" sx={{ cursor: 'pointer', mr: 0.5 }} onClick={clearSearch}>
                    <CloseChatIcon style={{opacity: 0.6 }} />
                  </InputAdornment>
                ) : null,
                sx: {
                  height: 42,
                  borderRadius: '8px',
                  backgroundColor: '#f3f4f6',
                  fontSize: '14px',
                  color: colors.text.primaryBlack,
                  '& input::placeholder': {
                    color: '#a1a8b5',
                    opacity: 1,
                    fontWeight: 400
                  },
                  '& fieldset': { border: 'none' }
                }
              }}
            />
          </Box>
          <Box sx={{ flex: 1, overflow: 'auto' }} id="chat-scroll-container">
            {searchQuery.trim() !== '' ? (
              isSearching ? (
                <GroupsSkeleton showSubLine={1} skeletonCount={4} />
              ) : (userSearchResults.length > 0 || messageSearchResults.length > 0) ? (
                <List sx={{ py: 0 }}>
                  {userSearchResults.length > 0 && (
                    <>
                      <Typography
                        fontSize={12}
                        fontWeight={600}
                        sx={{ px: 2, pt: 1, pb: 0.5, color: colors.text.approxNero, backgroundColor: '#f9f9f9' }}
                      >
                        People
                      </Typography>
                      {userSearchResults.map((_itm, index) => (
                        <ListItem
                          disablePadding
                          key={`user-${index}`}
                          sx={{ borderBottom: `1px solid ${colors.border.primary}` }}
                        >
                          <ListItemButton
                            onClick={() => handleUserResultClick(_itm)}
                            sx={{ display: 'flex', alignItems: 'center', py: 1, px: 2 }}
                          >
                            <AvatarImg
                              id="userImg"
                              enableLetterImg={!_itm.imageId}
                              alt={_itm.name}
                              src={_itm.imageUrl}
                              width={30}
                              height={30}
                              name={_itm.name}
                              nameStyle={{ fontSize: 12 }}
                              style={{ borderRadius: '50%', objectFit: 'cover' }}
                            />
                            <TypographyWithI18N
                              className="bodyTextSmall"
                              i18nKey={_itm.name}
                              sx={{ ml: 1.5, color: colors.text.primaryBlack, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                            />
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </>
                  )}

                  {messageSearchResults.length > 0 && (
                    <>
                      <Typography
                        fontSize={12}
                        fontWeight={600}
                        sx={{ px: 2, pt: 1, pb: 0.5, color: colors.text.approxNero, backgroundColor: '#f9f9f9' }}
                      >
                        Chats
                      </Typography>
                      {messageSearchResults.map((_itm, index) => (
                        <ListItem
                          disablePadding
                          key={`msg-${index}`}
                          sx={{ borderBottom: `1px solid ${colors.border.primary}` }}
                        >
                          <ListItemButton
                            onClick={() => handleMessageResultClick(_itm)}
                            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1, px: 2 }}
                          >
                            <Box display="flex" alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
                              <Box display="flex" justifyContent="start" sx={{ width: '30px', flexShrink: 0 }}>
                                {_itm.isGroup ? (
                                  <AvatarImg
                                    id="groupImg"
                                    enableLetterImg={!_itm.groupImageId}
                                    alt={_itm.groupName}
                                    src={groupImgUrl(_itm.groupImageId)}
                                    name={_itm.groupName}
                                    width={30}
                                    height={30}
                                    nameStyle={{ fontSize: 10 }}
                                    style={{ borderRadius: '50%', objectFit: 'cover' }}
                                  />
                                ) : (
                                  renderAvatarsForParticipants(_itm.participants)
                                )}
                              </Box>
                              <Box display="flex" flexDirection="column" marginLeft={1.5} sx={{ minWidth: 0, flex: 1 }}>
                                <TypographyWithI18N
                                  className={_itm.unreadCount !== 0 ? 'bodyTextSmallBold' : 'bodyTextSmall'}
                                  i18nKey={_itm.isGroup ? _itm.groupName : getDisplayNameForParticipants(_itm.participants)}
                                  sx={{ color: colors.text.primaryBlack, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                                />
                                {HighlightedMessage(_itm, searchQuery)}
                              </Box>
                            </Box>
                            <Box display="flex" flexDirection="column" alignItems="flex-end" sx={{ ml: 1, flexShrink: 0 }}>
                              <TypographyWithI18N
                                i18nKey={formatTimeDifferenceForMsg(_itm.message)}
                                fontSize={11}
                                sx={{ color: colors.text.approxSuvaGrey, whiteSpace: 'nowrap' }}
                              />
                              {_itm.unreadCount !== 0 && (
                                <Box
                                  sx={{
                                    display: 'inline-flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    backgroundColor: 'primary.main',
                                    color: 'primary.contrastText',
                                    borderRadius: '15px',
                                    height: '18px',
                                    width: '18px',
                                    fontSize: 11,
                                    fontWeight: 'bold',
                                    boxShadow: 1,
                                    mt: 0.5
                                  }}
                                >
                                  {_itm.unreadCount}
                                </Box>
                              )}
                            </Box>
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </>
                  )}
                </List>
              ) : (
                <Box sx={{ py: 4, px: 2, textAlign: 'center' }}>
                  <Typography fontSize={13} color={colors.text.approxSuvaGrey}>
                    No matching results found
                  </Typography>
                </Box>
              )
            ) : loader && !hasFetchedData ? (
              <GroupsSkeleton showSubLine={1} skeletonCount={8} />
            ) : conversationsData?.length > 0 ? (
              <InfiniteScroll
                dataLength={conversationsData?.length}
                next={getConversationsList}
                hasMore={pageChange?.nextPage}
                loader={loader && <GroupsSkeleton showSubLine={1} />}
                onScroll={onScrollEvent}
                scrollableTarget="chat-scroll-container"
                style={{
                  background: '#fff',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 16,
                }}
                scrollThreshold='80%'
              >
                <List>
                  {conversationsData.map((conversation, index) => {
                    const friend = conversation.participantIds.map((id: any) => friendData.find(friend => friend.userId === id));
                    const taggedGroup = taggedGroups.find(
                      (group) => conversation.groupId === group.id
                    );
                    return (
                      <FriendItem
                        key={index}
                        loggedUser={loggedUser}
                        friends={friend}
                        taggedGroup={taggedGroup}
                        conversation={conversation}
                        onClick={handleConversationClick}
                      />
                    );
                  })}
                </List>
              </InfiniteScroll>
            ) : (
              <Box
                sx={{ margin: 'auto' }}
                display='flex'
                flexDirection='column'
                alignItems='center'
              >
                <NoConversations style={{ width: '50%', height: '50%' }} />
                <Box>
                  <TypographyWithI18N
                    textAlign={'center'}
                    fontSize={14}
                    color={colors.text.primaryTheme}
                    i18nKey={'Every great conversation starts somewhere'}
                  />
                  <TypographyWithI18N
                    textAlign={'center'}
                    fontSize={12}
                    i18nKey={'Why not start yours now?'}
                  />
                </Box>
              </Box>
            )}
          </Box>
        </>
      )}
        {activeConversations.map((activeConversation, index) => (
          <ChatPopup
            key={activeConversation.conversationId}
            conversationId={activeConversation.conversationId}
            loggedUser={loggedUser}
            friends={activeConversation.friends}
            onClose={handleCloseChat}
            position={-index}
            onMessageSent={handleChatPopupMessageSent}
          />
        ))}

         {isCreateMessageOpen && (
            <CreateMessagePopup
              open={isCreateMessageOpen}
              onClose={() => setIsCreateMessageOpen(false)}
              onMessageSent={(conversationId: string) => {
                setIsCreateMessageOpen(false);
                getConversationSnapShot(conversationId)
                  .then((data) => {
                    if (data?.participants) {
                      setFriendData((prevFriends = []) => {
                        const combinedFriends = [...prevFriends, ...data.participants];
                        const uniqueFriends = Array.from(
                          new Map(combinedFriends.map((friend) => [friend.userId, friend])).values()
                        );
                        return uniqueFriends;
                      });

                    if (data.isAdminConversation && data.isGroupTagged && data.group) {
                      setTaggedGroupsData((prevGroups = []) => {
                        const combinedGroups = [...prevGroups, data.group];
                        const uniqueGroups = Array.from(
                          new Map(combinedGroups.map((group) => [group.id, group])).values()
                        );
                        return uniqueGroups;
                      });
                    }
                      const payload = {
                        size: 20,
                        nextPageCursor: ''
                      };
                     
                      getAllConversation(payload)
                      .then((freshData) => {
                        const res = freshData.content;
                        const conversations = res.conversationHistory.filter(
                          (conversation: any) => conversation.lastMessage != null && conversation.lastMessage.content !== null
                        );
                        const newConversation = conversations.find(
                          (conv: any) => conv.conversationId === conversationId
                        );
                    if (newConversation) {
                      setConversationsData((prevConversations) => {
                        const exists = prevConversations.some(
                          (conv) => conv.conversationId === conversationId
                        );

                        const updatedList = exists
                          ? prevConversations.map((conv) =>
                              conv.conversationId === conversationId
                                ? { ...conv, ...newConversation }
                                : conv
                            )
                          : [newConversation, ...prevConversations];

                        return updatedList.sort(
                          (a, b) =>
                            new Date(b.modifiedAt).getTime() -
                            new Date(a.modifiedAt).getTime()
                        );
                      });
                      setIsExpanded(true);
                      const friends = newConversation.participantIds
                      ?.map((id: any) => friendData.find((f) => f.userId === id))
                      .filter(Boolean) ?? [];
                      setTimeout(() => {
                        setActiveConversations(prev => {
                        const alreadyOpen = prev.find((ac) => ac.conversationId === conversationId);
                          if (alreadyOpen) {
                            return prev;
                          }    
                          if (prev.length >= 3) {
                            return [...prev.slice(1), { conversationId, friends }];
                          } else {
                            return [...prev, { conversationId, friends }];
                          }
                        });
                      }, 400);
                    }
                  })
                    .catch((err) => {
                    console.error("Error refetching conversations:", err);
                    });
                  }
                  })
                    .catch((err) => {
                    console.error("Error fetching conversation snapshot:", err);
                    setIsExpanded(true);
                    });
              }}
            />
          )}

    </Box>
  );
};

export default ChatsScreen;